/* eslint-disable no-console */
import {CheckOutlined, EditOutlined, RobotOutlined} from '@ant-design/icons';
import {Button} from 'antd';
import {marked} from 'marked';
import React, {useState} from 'react';

import {
  askAIPrompt,
  AskAIResponse
} from '../../../services/modules/AI/askAIAPI';
import {IField} from '../interface/inputType';

const promptStates = {
  Initial: 'Initial',
  thinking: 'thinking',
  writing: 'writing',
  done: 'done'
};

const toQuillHtml = (text: string) => {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  return `<p>${escaped.replace(/\n/g, '<br/>')}</p>`;
};

const markdownToQuillHtml = (text: string) => {
  try {
    const parsed = marked.parse(text, {async: false});

    return typeof parsed === 'string' ? parsed : toQuillHtml(text);
  } catch (error) {
    return toQuillHtml(text);
  }
};

interface PromptStepsProps {
  field: IField | any;
  getFieldValue: (name: string | string[]) => any;
  setFieldValue: (name: string | string[], value: any) => void;
  setSubmitDisabled?: (disabled: boolean) => void;
  userId: string;
  companyId: string;
  innerFieldName?: string | Array<string | number> | undefined;
}

const PromptSteps: React.FC<PromptStepsProps> = ({
  field,
  setFieldValue,
  setSubmitDisabled,
  userId,
  companyId,
  innerFieldName = undefined
}) => {
  const [promptState, setPromptState] = useState({
    state: promptStates.Initial
  });

  const fieldName = innerFieldName ?? field.fieldName;

  const handleAskAIClick = () => {
    setFieldValue(fieldName, '');
    setPromptState({state: promptStates.thinking});

    askAIPrompt(userId, companyId, field.askAIkey ?? '')
      .then((res: AskAIResponse | undefined) => {
        if (!res?.response) {
          setPromptState({state: promptStates.Initial});

          return;
        }

        setPromptState({state: promptStates.writing});

        const decodedText = res.response;

        let index = 0;
        let generatedText = '';

        const interval = setInterval(() => {
          const nextChar = decodedText.charAt(index);
          index++;
          generatedText += nextChar;

          if (index >= decodedText.length) {
            clearInterval(interval);

            setTimeout(() => {
              setPromptState({state: promptStates.Initial});
            }, 1000);

            setPromptState({state: promptStates.done});
          }

          setFieldValue(fieldName, markdownToQuillHtml(generatedText));
        }, 10); // 10ms for faster typing effect

        setSubmitDisabled?.(false);
      })
      .catch((error) => {
        console.error('Ask AI Error:', error);
        setPromptState({state: promptStates.Initial});
      })
      .finally(() => {
        // Optional final cleanup if needed
      });
  };

  const innerComponent = () => {
    switch (promptState.state) {
      case promptStates.Initial:
        return (
          <div
            style={{
              minHeight: '32px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Button
              type="primary"
              shape="round"
              icon={<RobotOutlined />}
              className="ask-ai-button"
              style={{
                backgroundColor: '#FFE3D1',
                borderColor: '#FFE3D1',
                color: '#994f1a',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600'
              }}
              onClick={handleAskAIClick}
            >
              Ask AI
            </Button>
          </div>
        );
      case promptStates.thinking:
        return (
          <div
            style={{
              fontWeight: 'bold',
              minHeight: '32px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            Thinking...
            <EditOutlined
              style={{
                backgroundColor: '#994f1a',
                borderColor: '#994f1a',
                color: '#FFF',
                borderRadius: '20px',
                fontSize: '20px',
                padding: '4px 14px',
                marginLeft: '10px'
              }}
            />
          </div>
        );
      case promptStates.writing:
        return (
          <div
            style={{
              fontWeight: 'bold',
              minHeight: '32px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            Writing...
            <EditOutlined
              style={{
                backgroundColor: '#994f1a',
                borderColor: '#994f1a',
                color: '#FFF',
                borderRadius: '20px',
                fontSize: '20px',
                padding: '4px 14px',
                marginLeft: '10px',
                transform: 'rotate(180deg)'
              }}
            />
          </div>
        );
      case promptStates.done:
        return (
          <div
            style={{
              fontWeight: 'bold',
              minHeight: '32px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            Done
            <CheckOutlined
              style={{
                backgroundColor: '#719a5d',
                borderColor: '#719a5d',
                color: '#FFF',
                borderRadius: '20px',
                fontSize: '14px',
                padding: '4px',
                marginLeft: '10px'
              }}
            />
          </div>
        );
      default:
        return <div></div>;
    }
  };

  return (
    <div
      id={innerFieldName ?? field.fieldName}
      style={{
        display: 'flex',
        justifyContent: 'end',
        alignItems: 'center',
        padding: '10px',
        borderRadius: '6px',
        borderTopLeftRadius: '0',
        borderTopRightRadius: '0',
        borderLeft: '1px solid #d9d9d9',
        borderRight: '1px solid #d9d9d9',
        borderBottom: '1px solid #d9d9d9'
      }}
    >
      {innerComponent()}
    </div>
  );
};

export default PromptSteps;
